import { z } from 'zod';
import { PaginationSchema } from './general';

export const ItemHistory = z.array(
  z.object({
    eventTimestamp: z.string(),
    userName: z.string().nullable(),
    action: z.string(),
    categoryName: z.string().nullable(),
    externalCategoryId: z.string().nullable(),
  }),
);

const itemCountrySpecific = z.object({
  itemNo: z.string(),
  itemType: z.string(),
  itemName: z.string().optional(),
  itemNameOfficial: z.string(),
});

const itemCountrySpecificTransformed = itemCountrySpecific.transform((item) => ({
  ...item,
  itemName: item.itemNameOfficial,
}));

const itemCountrySpecificList = z
  .array(itemCountrySpecificTransformed)
  .nullable()
  .optional();

export const AutoCategorizedItemSchema = z.object({
  itemNo: z.string(),
  itemType: z.string(),
  itemNameOfficial: z.string(),
  homeFurnishingBusinessName: z.string().nullable(),
  homeFurnishingBusinessNo: z.string().nullable(),
  categoryId: z.number(),
  externalCategoryId: z.string(),
  categoryName: z.string(),
  eventTimestamp: z.string(),
  source: z.string(),
  itemCountrySpecificList: z.array(z.string()).nullable(),
});

export const AutoCategorizedItemsReport = PaginationSchema.extend({
  content: z.array(AutoCategorizedItemSchema),
});

export const ItemCategorizedStatisticsSchema = z.object({
  auto: z.number(),
  total: z.number(),
});

export const ItemDetails = z.object({
  countrySpecificItem: z.boolean(),
  itemNo: z.string(),
  itemType: z.string(),
  itemNameOfficial: z.string(),
  startDateSale: z.string().nullable(),
  endDateSale: z.string().nullable(),
  productName: z.string().nullable(),
  productTypeName: z.string().nullable(),
  homeFurnishingBusinessName: z.string().nullable(),
  homeFurnishingBusinessNo: z.string().nullable(),
  productAreaName: z.string().nullable(),
  productAreaNo: z.string().nullable(),
  imageName: z.string().nullable(),
  validForStructure: z.boolean(),
  showable: z.boolean(),
  itemStyleGroup: z.string().nullable(),
  mainParentDTOs: z
    .array(
      z.object({
        parentCategoryId: z
          .number()
          .transform((val) => `${val}`)
          .nullable(),
        parentCategoryName: z.string().nullable(),
        parentExternalCategoryId: z.string().nullable(),
        mainParent: z.boolean(),
      }),
    )
    .nullable(),
  itemContentDTOList: z
    .array(
      z.object({
        itemNo: z.string(),
        itemType: z.string().nullable(),
        itemName: z.string().nullable(),
        itemQuantity: z.number(),
      }),
    )
    .nullable(),
  itemCountrySpecificList,
});

export const ItemSchema = z
  .object({
    itemNo: z.string(),
    itemType: z.string().nullable(),
    itemName: z.string().nullable(),
    countrySpecificItemCount: z.number().optional(),
    itemCountrySpecificList,
    productImageCategoryIds: z
      .array(z.number().transform((val) => `${val}`))
      .nullable()
      .optional(),
    mainParent: z
      .number()
      .transform((val) => `${val}`)
      .optional()
      .nullable(),
  })
  .transform((item) => ({
    ...item,
    countrySpecificItemCount:
      item?.countrySpecificItemCount ?? (item.itemCountrySpecificList?.length || 0),
  }));

export const ItemsSchema = PaginationSchema.extend({
  content: z.array(ItemSchema),
});

export const ItemHistorySchema = PaginationSchema.extend({
  content: ItemHistory,
});

export const UncategorizedItemsSchema = z.array(
  z.object({
    itemNo: z.string(),
    itemType: z.string(),
    itemName: z.string(),
    productName: z.string().nullable(),
    salesStartDate: z.string().nullable(),
    countrySpecificItemCount: z.number().nullable(),
    suggestedCategories: z.array(
      z.object({
        externalCategoryId: z.string(),
        categoryId: z.number().transform((val) => `${val}`),
        globalCategoryName: z.string(),
      }),
    ),
    inDraft: z.boolean(),
  }),
);

export const AddItemsSchema = z.object({
  addedItems: z.array(
    z
      .object({
        itemName: z.string(),
        itemNo: z.string(),
        itemType: z.string(),
        mainParent: z
          .number()
          .transform((val) => `${val}`)
          .optional()
          .nullable(),
      })
      .nullable(),
  ),
  existingItems: z.array(
    z
      .object({
        itemName: z.string(),
        itemNo: z.string(),
        itemType: z.string(),
      })
      .nullable(),
  ),
  notValidForStructureItems: z.array(
    z
      .object({
        itemName: z.string(),
        itemNo: z.string(),
        itemType: z.string(),
      })
      .nullable(),
  ),
});

export const AddItemsToMultipleSchema = z.array(AddItemsSchema);

export const MissingItemsSchema = z.object({
  categories: z
    .array(
      z
        .object({
          categoryId: z.number().transform((val) => `${val}`),
          childCategories: z.array(z.number().transform((val) => `${val}`)),
          childItems: z.array(z.string()),
          externalCategoryId: z.string(),
          globalCategoryName: z.string(),
        })
        .transform((val) => ({
          ...val,
          childCategoryCount: val.childCategories?.length,
          childItemCount: val.childItems?.length || 0,
        })),
    )
    .nullable(),
  items: z.array(
    z.object({
      countrySpecificItemCount: z.number(),
      itemName: z.string(),
      itemNo: z.string(),
      itemType: z.string(),
      mainParent: z
        .number()
        .transform((val) => `${val}`)
        .nullable(),
    }),
  ),
  topCategories: z.array(z.number().transform((val) => `${val}`)).optional(),
});

export const ItemRelationsSchema = z.object({
  catalogueList: z.array(
    z
      .number()
      .transform((val) => `${val}`)
      .nullable(),
  ),
  categoryWithCountrySpecificItemList: z.array(
    z.object({
      categoryId: z.number().transform((val) => `${val}`),
      itemCountrySpecificList,
    }),
  ),
  itemName: z.string().nullable(),
  itemNo: z.string(),
  itemType: z.string().nullable(),
  notValidForStructure: z.boolean(),
  structureReasonText: z.string().nullable(),
  productImageCategoryIds: z
    .array(z.number().transform((val) => `${val}`))
    .nullable()
    .optional(),
  uncategorised: z.boolean(),
  mainParent: z
    .number()
    .transform((val) => `${val}`)
    .nullable()
    .optional(),
});

export const MarketSalesDateSchema = z.array(
  z.object({
    startDateSale: z.string().optional(),
    retailUnit: z.string(),
    showable: z.boolean(),
    endDateSale: z.string().optional(),
  }),
);

export const NotValidForStructureItemsSchema = PaginationSchema.extend({
  content: z.array(
    z.object({
      itemName: z.string(),
      itemNo: z.string(),
      itemType: z.string(),
      structureReasonText: z.string().nullable(),
    }),
  ),
});

export const NotValidForStructureReportsSchema = PaginationSchema.extend({
  content: z.array(
    z.object({
      itemNo: z.string(),
      itemType: z.string(),
      itemName: z.string(),
      homeFurnishingBusinessNo: z.string().nullable(),
      newValidForStructure: z.boolean(),
      newStructureReasonText: z.string(),
      oldValidForStructure: z.boolean(),
      oldStructureReasonText: z.string().nullable(),
      updatedDate: z.string(),
    }),
  ),
});

export const ItemGroupingReportsSchema = z.object({
  totalItemCount: z.number(),
  totalPercentageGrouped: z.number(),
  artPercentageGrouped: z.number(),
  sprPercentageGrouped: z.number(),
  hfbReportDTOList: z.array(
    z.object({
      hfbNo: z.string(),
      hfbName: z.string().nullable(),
      totalItemCount: z.number(),
      totalPercentageGrouped: z.number(),
      artPercentageGrouped: z.number(),
      sprPercentageGrouped: z.number(),
    }),
  ),
});

export const ItemImage = z.object({
  itemNo: z.string(),
  productName: z.string(),
  productType: z.string(),
  imageName: z.string(),
});
